/**
 * CostLabel — T24.
 *
 * Displays the generation status / cost in the output pane footer.
 *
 * States:
 *   - idle    → "Ready"
 *   - streaming → "Generating..."
 *   - done    → "Cost - $X.XX" (always 2dp)
 *   - error   → "Ready" (reset to ready state on error so user can retry)
 *   - copied  → "Copied" (2s transient state; caller sets this externally via prop)
 *
 * The "Copied" state is controlled by the parent (CopyButton triggers it).
 */
import { Text } from '@mantine/core';
import { formatCost } from '../../hooks/use-run-stream';
import type { User } from '../../state/auth-store';
import type { RunStreamStatus } from '../../state/run-stream-store';

interface CostLabelProps {
  cost: number | null;
  status: RunStreamStatus;
  role: User['role'];
  /** When true, overrides the label with "Copied" for 2s. */
  copied?: boolean;
}

export function CostLabel({ cost, status, role, copied = false }: CostLabelProps) {
  let label: string;

  if (copied) {
    label = 'Copied';
  } else {
    switch (status) {
      case 'streaming':
        label = 'Generating...';
        break;
      case 'done': {
        const formatted = formatCost(cost, role);
        label = formatted ? `Cost - ${formatted}` : 'Saved';
        break;
      }
      default:
        label = 'Ready';
        break;
    }
  }

  return (
    <Text
      fz="sm"
      c="dimmed"
      data-testid="cost-label"
      data-status={status}
      data-copied={String(copied)}
    >
      {label}
    </Text>
  );
}
